import { computed, h, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { defineStore } from 'pinia'


import { AssetClass } from '@/entities/assets'
import { ErrorNodes } from '@types'
import { EntityEvent } from '@/entities/utils/enums'

import { ASSET_FIELD } from '@/entities/assets/utils/const'
import { ROUTE_NAME } from '@/const'
import { NOTIFICATION_DELAY } from '@/const/common'

import { checkForErrors } from './utils/helpers'
import {
  createBunchElement,
  duplicateBunchElement,
  insertBunchElementAt,
  isDirtyList,
  normalizeSearchValue,
  unsavedGridBlocker,
  unshiftBunchElement,
} from '../utils/helpers'

import { useNotifications } from '@/plugins/notification'

import { useAssetsStore } from '../assets'
import { useContactsBunchStore } from '../contacts/bunch'
import { useDocumentsBunchStore } from '../documents/bunch'
import { useMainStore } from '@/store/main'
import { useModalsStore } from '../modals'
import { useRepositoriesStore } from '../repositories/index'
import { useTransactionsBunchStore } from '../transactions/bunch'

export const useAssetsBunchStore = defineStore('assets-bunch', () => {
  // INIT

  const mainStore = useMainStore()
  const modalsStore = useModalsStore()
  const assetsStore = useAssetsStore()
  const contactsBunchStore = useContactsBunchStore()
  const documentsBunchStore = useDocumentsBunchStore()
  const transactionsBunchStore = useTransactionsBunchStore()
  const repositoryStore = useRepositoriesStore()

  const router = useRouter()
  const { error } = useNotifications()

  // STORE

  const initFlag = ref(false)
  const list = ref<Map<string, AssetClass>>(new Map())
  const errorsCommon = ref<ErrorNodes[]>()
  const errorsCurrencies = ref<ErrorNodes[]>()
  const errorsIncomeAccounts = ref<ErrorNodes[]>()
  const errorsExpenseAccounts = ref<ErrorNodes[]>()

  // ACTIONS

  const buildElement = () => createBunchElement(AssetClass, {
    currency: repositoryStore.getCurrentCurrency,
    repository_id: repositoryStore.getCurrentId,
    tags: [],
  })

  const createElement = () => insertBunchElementAt(buildElement(), list)

  const createElementAt = (index: number) =>
    insertBunchElementAt(buildElement(), list, index)

  const unshiftElement = (element: AssetClass) => unshiftBunchElement(element, list)

  // A document belongs to one asset, so a copy starts without the original's documents and logo.
  const duplicateElement = (id: string) =>
    duplicateBunchElement(id, list, AssetClass, undefined, { docs: [], thumbnail: null })

  const showErrors = (result: ErrorNodes[] | undefined) => {
    if (!result) return
    error(
      { message: h('div', { class: 'space-y-2 text-sm sm:text-xs' }, result) },
      NOTIFICATION_DELAY,
    )
  }

  const checkForCommonErrors = () => {
    const result = checkForErrors(
      getCommonList.value,
      'Asset',
      'Assets',
      router,
    )
    errorsCommon.value = result
    showErrors(result)
    return !!result
  }

  const checkForCurrenciesErrors = () => {
    const result = checkForErrors(
      getCurrenciesList.value,
      'Currency',
      'Assets',
      router,
    )
    errorsCurrencies.value = result
    showErrors(result)
    return !!result
  }

  const checkForIncomeAccountsErrors = () => {
    const result = checkForErrors(
      getIncomeAccountsList.value,
      'Income account',
      'Assets',
      router,
      false,
    )
    errorsIncomeAccounts.value = result
    showErrors(result)
    return !!result
  }

  const checkForExpenseAccountsErrors = () => {
    const result = checkForErrors(
      getExpenseAccountsList.value,
      'Expense Account',
      'Assets',
      router,
      false,
    )
    errorsExpenseAccounts.value = result
    showErrors(result)
    return !!result
  }

  // GETTERS

  const getList = computed<Map<string, AssetClass>>(() =>
    list.value.size ? list.value : new Map(),
  )
  const getEntries = computed(() => Array.from(getList.value.entries()))
  const getCommonList = computed(() => {
    return new Map(getEntries.value.filter(([, instance]) => instance.isCommon))
  })
  const getCurrenciesList = computed(() => {
    return new Map(
      getEntries.value.filter(([, instance]) => instance.isCurrency),
    )
  })
  const getIncomeAccountsList = computed(() => {
    return new Map(
      getEntries.value.filter(([, instance]) => instance.isIncomeAccount),
    )
  })
  const getExpenseAccountsList = computed(() => {
    return new Map(
      getEntries.value.filter(([, instance]) => instance.isExpenseAccount),
    )
  })
  const getUnlinkedList = computed(() => {
    return new Map(
      getEntries.value.filter(([, instance]) => !instance.isLinked),
    )
  })

  const assetsList = computed(() => assetsStore.getList)

  const getCommonErrors = computed(() => errorsCommon.value)
  const getCurrenciesErrors = computed(() => errorsCurrencies.value)
  const getIncomeAccountsErrors = computed(() => errorsIncomeAccounts.value)
  const getExpenseAccountsErrors = computed(() => errorsExpenseAccounts.value)

  const getElementByName = (name: string, skippedId?: string) => {
    const normalizedName = normalizeSearchValue(name)
    if (!normalizedName) return

    return Array.from(getList.value.values()).find(
      instance =>
        normalizeSearchValue(instance.field<string>(ASSET_FIELD.NAME).value) ===
          normalizedName && instance.id !== skippedId,
    )
  }

  const getElementByTicker = (ticker: string, skippedId?: string) => {
    const normalizedTicker = normalizeSearchValue(ticker)
    if (!normalizedTicker) return

    return Array.from(getList.value.values()).find(
      instance =>
        normalizeSearchValue(instance.field<string>(ASSET_FIELD.TICKER).value) ===
          normalizedTicker && instance.id !== skippedId,
    )
  }

  const getElementById = (id: string) => {
    return getList.value.get(id) as AssetClass
  }

  const getNameById = (id: string) => {
    return getElementById(id)?.field<string>(ASSET_FIELD.NAME).value
  }

  const getTypeById = (id: string) => {
    return getElementById(id)?.field<string>(ASSET_FIELD.TYPE).value
  }

  const isCommonDirty = computed(() => isDirtyList(getCommonList.value))
  const isCurrenciesDirty = computed(() => isDirtyList(getCurrenciesList.value))
  const isIncomeAccountsDirty = computed(() =>
    isDirtyList(getIncomeAccountsList.value),
  )
  const isExpenseAccountsDirty = computed(() =>
    isDirtyList(getExpenseAccountsList.value),
  )

  // FILL STORE

  watch(assetsList, value => {
    const isFirstLoading = list.value.size === 0
    // TODO: implement the same logic for all the bunches or make a decision how to handle update after reload the data better
    if (!isFirstLoading) {
      // Skip new assets
      const incomingIds = new Set(value.map(item => item.id))
      list.value = new Map(Array.from(getList.value.entries()).filter(([id, instance]) => instance.isNew || incomingIds.has(id)))
    }
    value.forEach(item => {
      const asset = list.value.get(item.id)
      if (!asset) {
        const instance = new AssetClass(item)
        if (isFirstLoading) {
          list.value.set(item.id, instance)
        } else {
          unshiftElement(instance)
        }
        instance.validateData()
        instance.addEventListener(EntityEvent.UPDATED, (data, originalData) => {
          if (data.type === originalData.type) return
          transactionsBunchStore.validateAll()
        })
      } else {
        asset.forceUpdate(item)
      }
    })
    initFlag.value = true
  })

  watch(getList, () => {
    for (const instance of contactsBunchStore.getList.values()) {
      instance.resetAssets()
    }
    for (const instance of documentsBunchStore.getList.values()) {
      instance.resetAssetId()
    }
  })

  // ADD BLOCKER IF DIRTY

  watch(isCommonDirty, value => {
    if (value) {
      mainStore.addBlocker(
        `${ROUTE_NAME.ASSETS}-hasChanges`,
        unsavedGridBlocker('assets', () => {
          router.push({ name: ROUTE_NAME.ASSETS })
        },
        () => {
          modalsStore.closeModals(AssetClass, (entity: AssetClass) => entity.isCommon)
          getCommonList.value.forEach(item => {
            item.cancel()
          })
        }
        ),
      )
    } else {
      mainStore.removeBlocker(`${ROUTE_NAME.ASSETS}-hasChanges`)
    }
  })

  watch(isCurrenciesDirty, value => {
    if (value) {
      mainStore.addBlocker(
        `${ROUTE_NAME.CURRENCIES}-hasChanges`,
        unsavedGridBlocker('currencies', () => {
          router.push({ name: ROUTE_NAME.CURRENCIES })
        },
        () => {
          modalsStore.closeModals(AssetClass, (entity: AssetClass) => entity.isCurrency)
          getCurrenciesList.value.forEach(item => {
            item.cancel()
          })
        }
        ),
      )
    } else {
      mainStore.removeBlocker(`${ROUTE_NAME.CURRENCIES}-hasChanges`)
    }
  })

  watch(isIncomeAccountsDirty, value => {
    if (value) {
      mainStore.addBlocker(
        `${ROUTE_NAME.INCOME_ACCOUNTS}-hasChanges`,
        unsavedGridBlocker('incomeAccounts', () => {
          router.push({ name: ROUTE_NAME.INCOME_ACCOUNTS })
        },
        () => {
          modalsStore.closeModals(AssetClass, (entity: AssetClass) => entity.isIncomeAccount)
          getIncomeAccountsList.value.forEach(item => {
            item.cancel()
          })
        }
        ),
      )
    } else {
      mainStore.removeBlocker(`${ROUTE_NAME.INCOME_ACCOUNTS}-hasChanges`)
    }
  })

  watch(isExpenseAccountsDirty, value => {
    if (value) {
      mainStore.addBlocker(
        `${ROUTE_NAME.EXPENSE_ACCOUNTS}-hasChanges`,
        unsavedGridBlocker('expenseAccounts', () => {
          router.push({ name: ROUTE_NAME.EXPENSE_ACCOUNTS })
        }, () => {
          modalsStore.closeModals(AssetClass, (entity: AssetClass) => entity.isExpenseAccount)
          getExpenseAccountsList.value.forEach(item => {
            item.cancel()
          })
        }),
      )
    } else {
      mainStore.removeBlocker(`${ROUTE_NAME.EXPENSE_ACCOUNTS}-hasChanges`)
    }
  })

  const validateAll = () => {
    list.value.forEach(item => {
      item.validateData()
    })
  }

  // CLEAR STORE

  const clear = () => {
    initFlag.value = false
    list.value = new Map()
    errorsCommon.value = undefined
    errorsCurrencies.value = undefined
    errorsIncomeAccounts.value = undefined
    errorsExpenseAccounts.value = undefined
  }

  return {
    initFlag,

    createElement,
    createElementAt,
    unshiftElement,
    duplicateElement,
    checkForCommonErrors,
    checkForCurrenciesErrors,
    checkForIncomeAccountsErrors,
    checkForExpenseAccountsErrors,

    getList,
    getCommonList,
    getCurrenciesList,
    getIncomeAccountsList,
    getExpenseAccountsList,
    getUnlinkedList,
    getCommonErrors,
    getCurrenciesErrors,
    getIncomeAccountsErrors,
    getExpenseAccountsErrors,

    getElementByName,
    getElementByTicker,
    getElementById,
    getNameById,
    getTypeById,

    isCommonDirty,
    isCurrenciesDirty,
    isIncomeAccountsDirty,
    isExpenseAccountsDirty,

    validateAll,

    clear,
  }
})
